import { Empty, SelectProps, Skeleton, Tooltip } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import classnames from 'classnames';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Column, ColumnShape } from 'react-base-table';

import { SearchOutlined } from '@ant-design/icons';
import {
  IMappedStatusRowData,
  isDeployed,
  isMapped,
} from '@components/common/AutoCompleteInput/buildOptionsWithTag';
import { DimensionKebab } from '@components/common/DimensionTable/components/DimensioKebab';
import EditUserDefinedDimensionMenu from '@components/common/DimensionTable/components/EditUserDefinedDimensionMenu';
import { useDimensionPanelStore } from '@components/dimensions/store';
import { ETableSize, IDimensionInfo, eventDimensionNames } from '@components/dimensions/typed';
import { useQueryDimensionData } from '@components/mapping-panel/api';
import useScreenSize from '@hooks/useScreenSize';
import { blockedCanMapWhiteList } from '@src/common/constant/emp';
import {
  EDimensionType,
  ResetDimensionForMappedEvent,
  disshowDropdownDimensionType,
  eventTypeOfMappedEvent,
  mappedEventDeployedStatus,
  mappedEventDimensionType,
} from '@src/common/constant/enum';
import { MappedEventTypes } from '@src/common/types/empTypes';
import { useGetCustomDimension } from '@src/emp-3.0/api/queryHooks';
import { toLowerCase } from '@src/emp-3.0/utils';
import { humanize } from '@utils';

import BaseTable from '../BaseTable';
import AddEllipsisWithTooltip from '../EllipsisTooltip/AddEllipsisWithTooltip';
import { AllEventMenu } from './components/AllEventMenu';
import { Checkbox } from './components/Checkbox';
import MappedEventMenu from './components/MappedEventMenu';
import RenameDimensionMenu from './components/RenameDimensionMenu';
import ResponsiveStatusTag from './components/ResponsiveStatusTag';
import { SearchInput } from './components/SearchInput';
import TableHeader from './components/TableHeader';
import { ESorterOrder, ESorterType, ISorterOrderWithType } from './components/TableHeader/typed';
import TableSubHeader from './components/TableSubHeader';
import {
  ETableDeployedFilter,
  ETableMappedFilter,
  ITableSubHeader,
} from './components/TableSubHeader/typed';
import Title, { ETitleColor } from './components/Title';
import styles from './index.less';

interface IRowData {
  dimensionName: string;
  name: string;
  value: number;
}

interface ITable {
  dimension?: IDimensionInfo;
  dimensionList?: any[];
  lock?: boolean;
  loading?: boolean;
  data?: any[];
  titleDefaultValue?: string;
  titleOptions?: SelectProps['options'];
  titleColor?: ETitleColor;
  onTitleChange?: (value: string) => void;
  mappedStatusList?: IMappedStatusRowData[];
  customDimensionList?: any[];
  deployedStatusList?: any[];
  blockedEventsData?: string[];
  mappedEventDimensionData?: MappedEventTypes[];
  selectedList?: string[];
  onRowClick?: ({ dimensionName, name, value }: IRowData) => void;
  showCheckbox?: boolean;
  onCheckboxClick?: (checked: boolean, name: string) => void;
  rowHighlight?: boolean;
  showSelectAll?: boolean | number;
  onSelectAll?: (list: string[]) => void;
  selectAllLimit?: number;
  selectAllRemainCount?: number;
  transformedData?: any;
  sortByEventStatus?: boolean;
  onTitleFilterClick?: ITableSubHeader['onClick'];
  enableAction?: boolean;
}

const NUMBER_KEY = 'app_total_user_events';

const Table = (props: ITable) => {
  const {
    dimension,
    dimensionList = [],
    lock,
    titleDefaultValue,
    titleOptions,
    titleColor,
    onTitleChange,
    data,
    mappedStatusList,
    deployedStatusList,
    blockedEventsData,
    mappedEventDimensionData,
    loading = false,
    selectedList = [],
    onRowClick,
    showCheckbox = false,
    onCheckboxClick,
    rowHighlight = true,
    showSelectAll = false,
    onSelectAll,
    selectAllLimit = 0,
    selectAllRemainCount = 0,
    transformedData = {},
    sortByEventStatus = false,
    onTitleFilterClick,
    customDimensionList = [],
    enableAction = true,
  } = props;

  const { screenWidth: currentScreenWidth } = useScreenSize();
  const [size] = useDimensionPanelStore((state) => [state.size]);

  const [dimensionKey, setDimensionKey] = useState<string>(titleDefaultValue ?? '');
  const [dimensionData, setDimensionData] = useState<any[]>(data ?? []);
  const [tableData, setTableData] = useState<any[]>([]);
  const [sorterOrder, setSorterOrder] = useState<ISorterOrderWithType>({
    sorterOrder: ESorterOrder.NORMAL,
    type: ESorterType.NAME,
  });
  const [searchOpen, setSearchOpen] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [activeFilter, setActiveFilter] = useState<
    ETableMappedFilter | ETableDeployedFilter | null
  >(ETableMappedFilter.ALL);
  const [selectAllChecked, setSelectAllChecked] = useState<boolean>(false);
  const [mappableEventNumber, setMappableEventNumber] = useState<number>(+Infinity);

  const { data: totalEventsData, isFetching: totalEventsLoading } = useQueryDimensionData({}, 0);

  const customDimension = useMemo(
    () =>
      dimensionList?.find(
        (item) =>
          item.dimension_name === dimension?.dimensionName &&
          item.dimension_type === EDimensionType.USER_DEFINED,
      ),
    [dimension?.dimensionName, dimensionList],
  );

  const { data: customDimensionData } = useGetCustomDimension({
    custom_dimension_id: customDimension?.dimension_id,
  });

  const totalEvents = useMemo(() => {
    return totalEventsData?.[0]?.[NUMBER_KEY];
  }, [totalEventsData]);

  const allMappedEvent = useMemo(() => {
    const res = {};
    mappedEventDimensionData?.forEach((v) => {
      res[v.event_name] = v;
    });

    return res;
  }, [mappedEventDimensionData, dimensionKey]);

  const showTitleFilter = useMemo(
    () =>
      (dimension && !eventDimensionNames.includes(dimension.dimensionName)) ||
      (!!titleDefaultValue && !eventDimensionNames.includes(titleDefaultValue)),
    [dimension, titleDefaultValue],
  );

  const showTotalEventNumber = !!eventTypeOfMappedEvent[dimensionKey];

  const isCustomDimension = dimension?.dimensionType === EDimensionType.CUSTOM_DIMENSION;
  const isUserDefinedDimension = dimension?.dimensionType === EDimensionType.USER_DEFINED;

  const showSubHeader = !!mappedEventDimensionType[dimensionKey] || isCustomDimension;
  const showActionColumn =
    enableAction &&
    (dimensionKey === ResetDimensionForMappedEvent.mapped_event ||
      eventTypeOfMappedEvent[dimensionKey] ||
      isCustomDimension ||
      dimensionKey === mappedEventDimensionType.merged_event_tag_key);

  useEffect(() => {
    setDimensionData(data ?? []);
  }, [data]);

  useEffect(() => {
    setDimensionKey(titleDefaultValue ?? '');
  }, [titleDefaultValue]);

  const finalSelectAllCount = Math.min(selectAllLimit, selectAllRemainCount, mappableEventNumber);
  useEffect(() => {
    if (selectedList.length < finalSelectAllCount) {
      setSelectAllChecked(false);
    }
  }, [selectedList, finalSelectAllCount]);

  const filteredBlockedEventsData = useMemo(
    () => (blockedEventsData ?? []).filter((event) => !blockedCanMapWhiteList.includes(event)),
    [blockedEventsData],
  );

  useEffect(() => {
    let count = 0;
    tableData.forEach((data) => {
      const eventName = data[dimensionKey];
      if (
        !(filteredBlockedEventsData ?? []).includes(eventName) &&
        !isMapped(eventName, mappedStatusList)
      ) {
        count++;
      }
    });
    setMappableEventNumber(count);
  }, [tableData, filteredBlockedEventsData, mappedStatusList]);

  useEffect(() => {
    // Handle table filter
    let tableData = dimensionData.filter((event) => {
      if (activeFilter === ETableMappedFilter.Mapped) {
        return isMapped(event?.[dimensionKey], mappedStatusList);
      } else if (activeFilter === ETableMappedFilter.Unmapped) {
        return !isMapped(event?.[dimensionKey], mappedStatusList);
      } else if (activeFilter === ETableDeployedFilter.Deployed) {
        return isDeployed(event?.[dimensionKey], deployedStatusList);
      } else if (activeFilter === ETableDeployedFilter.Undeployed) {
        return !isDeployed(event?.[dimensionKey], deployedStatusList);
      } else {
        return true;
      }
    });

    // Handle sort by event status, in quick map panel mapped event and blocked event should put to the bottom
    if (sortByEventStatus) {
      const todoEventList: any[] = [];
      const mappedEventList: any[] = [];
      const blockedEventList: any[] = [];
      tableData.forEach((event) => {
        const eventName = event?.[dimensionKey];
        const eventIsBlocked = (filteredBlockedEventsData ?? []).includes(eventName);
        const eventIsMapped = isMapped(eventName, mappedStatusList);
        if (eventIsMapped) {
          mappedEventList.push(event);
        } else if (eventIsBlocked) {
          blockedEventList.push(event);
        } else {
          todoEventList.push(event);
        }
      });
      tableData = [...todoEventList, ...mappedEventList, ...blockedEventList];
    }

    // Handle sorter order
    let key = NUMBER_KEY;
    if (sorterOrder.type === ESorterType.EVENT) {
      key = NUMBER_KEY;
    } else if (sorterOrder.type === ESorterType.NAME) {
      key = dimensionKey;
    }
    tableData.sort((prev, cur) => {
      if (sorterOrder.sorterOrder === ESorterOrder.ASC) {
        return prev[key] > cur[key] ? 1 : -1;
      }
      if (sorterOrder.sorterOrder === ESorterOrder.DESC) {
        return prev[key] > cur[key] ? -1 : 1;
      }
      return 0;
    });

    if (searchText) {
      tableData = tableData.filter((event) =>
        toLowerCase(event?.[dimensionKey]).includes(toLowerCase(searchText)),
      );
    }
    setTableData(tableData);
  }, [
    dimensionData,
    mappedStatusList,
    sorterOrder,
    activeFilter,
    dimensionKey,
    searchText,
    deployedStatusList,
    filteredBlockedEventsData,
    sortByEventStatus,
  ]);

  const rowEventClick = useCallback(
    ({ rowData }) => {
      const name = rowData[dimensionKey];
      const value = rowData[NUMBER_KEY];
      onRowClick?.({ dimensionName: dimensionKey, name, value });
    },
    [dimensionKey, onRowClick],
  );

  const handleTitleChange = (value) => {
    setDimensionKey(value);
    onTitleChange?.(value);
  };

  const handleSorterClick = (value) => {
    setSorterOrder(value);
  };

  const handleFilterClick = (value) => {
    setActiveFilter(value);
  };

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const tagType = (key: string) => {
    if (dimensionKey !== disshowDropdownDimensionType.mapped_event && !isCustomDimension) {
      return;
    }
    let event;
    if (isCustomDimension) {
      event = transformedData?.[dimensionKey]?.find((item) => item.name === key);
    } else {
      event = allMappedEvent[key];
    }
    if (event) {
      const lessThanDay = moment().isBefore(moment(event.created_at * 1000).add(1, 'd'));
      if (event.status === mappedEventDeployedStatus.undeployed && lessThanDay) {
        return isCustomDimension ? 'RENAME NEW' : 'NEW';
      } else if (event.status === mappedEventDeployedStatus.undeployed) {
        return 'Undeployed';
      }
    }
    return '';
  };

  const nameCellRenderer: ColumnShape['cellRenderer'] = useCallback(
    (props) => {
      const { rowData, column } = props;
      const cellData = rowData[column.dataKey];
      const isMappedEvent =
        mappedEventDimensionType[dimensionKey] && isMapped(cellData, mappedStatusList);
      const showBlocked =
        eventTypeOfMappedEvent[dimensionKey] && (blockedEventsData ?? []).includes(cellData);
      const isBlocked = (filteredBlockedEventsData ?? []).includes(cellData);
      const isUserDefinedCustomDimension =
        customDimensionList?.find((dimension) => dimension.attribute_name === cellData) &&
        dimensionKey === mappedEventDimensionType.merged_event_tag_key;
      const showConvivaTag =
        allMappedEvent[cellData]?.storage_predefined_mapped_event &&
        dimension?.dimensionName === 'mapped_event';
      const disabled = isBlocked || isMappedEvent;
      return (
        <div className={styles.nameCellWrapper}>
          <div className={styles.nameCellTextWrapper}>
            {showCheckbox && (
              <Checkbox
                borderColor={'#0046ff'}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  onCheckboxClick?.(e.target.checked, cellData);
                }}
                checked={selectedList?.includes(cellData)}
                disabled={disabled}
                style={{ marginRight: 6 }}
              />
            )}
            <span
              className={classnames(styles.nameCellText, {
                [styles.nameCellTextActive]: selectedList?.includes(cellData),
                [styles.nameCellTextDisabled]: showCheckbox && disabled,
              })}
              onClick={() => rowEventClick(props)}
            >
              <AddEllipsisWithTooltip
                copyable
                text={cellData ?? rowData[column.dataKey]}
                dataIndex="name"
              />
            </span>
          </div>
          {isUserDefinedCustomDimension ? (
            <ResponsiveStatusTag text="Dimension" currentScreenWidth={currentScreenWidth} />
          ) : null}
          {isMappedEvent && activeFilter === ETableMappedFilter.ALL ? (
            <ResponsiveStatusTag text="Mapped" currentScreenWidth={currentScreenWidth} />
          ) : null}
          {showBlocked ? (
            <ResponsiveStatusTag
              text="Blocked"
              currentScreenWidth={currentScreenWidth}
              type="blocked"
            />
          ) : null}
          {showConvivaTag ? (
            <ResponsiveStatusTag
              text="Conviva"
              tooltip="Conviva predefined"
              currentScreenWidth={currentScreenWidth}
              type="conviva-predefined"
              alwaysShowTooltip
            />
          ) : null}
          {tagType(cellData) === 'NEW' ? <ResponsiveStatusTag text="NEW" type="new" /> : null}
          {tagType(cellData) === 'RENAME NEW' ? (
            <ResponsiveStatusTag text="NEW" type="rename-new" />
          ) : null}
          {tagType(cellData) === 'Undeployed' ? (
            <ResponsiveStatusTag text="Undeployed" type="undeployed" />
          ) : null}
        </div>
      );
    },
    [
      dimensionKey,
      mappedStatusList,
      blockedEventsData,
      filteredBlockedEventsData,
      customDimensionList,
      showCheckbox,
      selectedList,
      currentScreenWidth,
      activeFilter,
      tagType,
      onCheckboxClick,
      rowEventClick,
    ],
  );

  const valueCellRenderer: ColumnShape['cellRenderer'] = ({ cellData }) => {
    return <span className={styles.valueCellText}>{humanize(cellData, '')}</span>;
  };

  const actionCellRender: ColumnShape['cellRenderer'] = ({ cellData }) => {
    if (dimensionKey === ResetDimensionForMappedEvent.mapped_event) {
      return <MappedEventMenu event={allMappedEvent[cellData]} />;
    } else if (isCustomDimension) {
      const data = transformedData[dimensionKey];
      const event = data?.find((e) => e.name === cellData);
      return <RenameDimensionMenu event={event} />;
    } else if (eventTypeOfMappedEvent[dimensionKey]) {
      const showMapped =
        eventTypeOfMappedEvent[dimensionKey] && isMapped(cellData, mappedStatusList);
      return <AllEventMenu eventName={cellData} mappedStatus={showMapped} />;
    } else if (dimensionKey === mappedEventDimensionType.merged_event_tag_key) {
      let customDimension = customDimensionList?.find(
        (dimension) => dimension.attribute_name === cellData,
      );
      if (!customDimension) {
        customDimension = {
          attribute_name: cellData,
        };
      }
      return <DimensionKebab dimension={customDimension} />;
    }
    return '';
  };

  // Columns https://autodesk.github.io/react-base-table/api/column
  const columns: ColumnShape[] = useMemo(() => {
    // How to get state in cell render
    // https://github.com/Autodesk/react-base-table#closure-problem-in-custom-renderers
    const columns = [
      {
        title: 'Name',
        key: dimensionKey,
        dataKey: dimensionKey,
        className: styles.tableColumnNameCell,
        width: '100%',
        flexShrink: 1,
        align: Column.Alignment.LEFT,
        cellRenderer: nameCellRenderer,
        mappedStatusList,
        blockedEventsData,
        selectedList,
        activeFilter,
        currentScreenWidth,
        allMappedEvent,
        transformedData,
        rowEventClick,
        customDimensionList,
      },
      {
        title: 'Total Event',
        key: NUMBER_KEY,
        dataKey: NUMBER_KEY,
        className: styles.tableColumnEventNumberCell,
        width: 54,
        flexShrink: 0,
        align: Column.Alignment.RIGHT,
        cellRenderer: valueCellRenderer,
        allMappedEvent,
      },
    ] as ColumnShape[];

    if (showActionColumn) {
      columns.push({
        title: 'Action',
        key: 'Action',
        dataKey: dimensionKey,
        className: styles.tableColumnActionCell,
        width: 28,
        flexShrink: 0,
        align: Column.Alignment.RIGHT,
        cellRenderer: actionCellRender,
        allMappedEvent,
        transformedData,
      });
    }

    return columns;
  }, [
    dimensionKey,
    nameCellRenderer,
    mappedStatusList,
    blockedEventsData,
    selectedList,
    activeFilter,
    currentScreenWidth,
    allMappedEvent,
    transformedData,
    rowEventClick,
    customDimensionList,
    showActionColumn,
    actionCellRender,
  ]);

  const handleSearchClick = () => {
    setSearchOpen(true);
  };

  const handleCloseClick = () => {
    setSearchText('');
    setSearchOpen(false);
  };

  const handleSelectAllClick = (e: CheckboxChangeEvent) => {
    const checked = e.target.checked;
    setSelectAllChecked(checked);
    if (checked) {
      const res: string[] = [...selectedList];
      for (const item of tableData) {
        if (res.length >= finalSelectAllCount) {
          break;
        }
        const name = item[dimensionKey];
        if (
          (filteredBlockedEventsData ?? []).includes(name) ||
          isMapped(name, mappedStatusList) ||
          selectedList.includes(name)
        ) {
          continue;
        }
        res.push(name);
      }
      onSelectAll?.(res);
      return;
    }
    onSelectAll?.([]);
  };

  const getTableComponent = () => {
    if (loading) {
      return <Skeleton active={false} title={false} paragraph={{ rows: 11 }} />;
    }
    if (isCustomDimension && transformedData?.[dimensionKey]?.length === 0) {
      return (
        <Empty
          style={{ marginTop: '100px' }}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<span>Not yet created.</span>}
        />
      );
    }

    let columnLength = 8;
    let width = '100%';
    if (size === ETableSize.AUTO) {
      columnLength = 8;
    } else if (size === ETableSize.LARGE) {
      columnLength = 10;
    } else if (size === ETableSize.MEDIUM) {
      columnLength = 14;
    }
    if (tableData.length >= columnLength) {
      width = 'calc(100% + 20px)';
    }
    return (
      <div style={{ width, height: '100%' }}>
        <BaseTable
          columns={columns}
          data={tableData}
          headerHeight={0}
          rowKey={dimensionKey}
          rowClassName={({ rowData }) => {
            return classnames({
              [styles.tableRowActive]:
                rowHighlight && selectedList?.includes(rowData[dimensionKey]),
            });
          }}
          getScrollbarSize={() => 24}
        />
      </div>
    );
  };

  return (
    <div className={styles.tableContainer}>
      <div className={styles.titleContainer}>
        <Title
          lock={lock}
          isUserDefinedDimension={isUserDefinedDimension}
          showTitleFilter={showTitleFilter}
          loading={loading}
          value={titleDefaultValue}
          options={titleOptions}
          onChange={handleTitleChange}
          color={titleColor}
          onClick={onTitleFilterClick}
        />
        <div style={{ flexShrink: 0, marginLeft: 4 }}>
          {showSubHeader || searchOpen ? null : (
            <SearchOutlined style={{ width: '20px' }} onClick={handleSearchClick} />
          )}
          {enableAction && isUserDefinedDimension && (
            <EditUserDefinedDimensionMenu customDimension={customDimensionData?.data} />
          )}
        </div>
      </div>
      <TableHeader
        loading={totalEventsLoading}
        openNameSort={false}
        count={totalEvents}
        onClick={handleSorterClick}
        showTotal={showTotalEventNumber}
      />
      <div className={styles.subHeaderContainer}>
        {showSubHeader && (
          <TableSubHeader
            type={isCustomDimension ? 'Deployed' : 'Mapped'}
            onClick={handleFilterClick}
            onSearchClick={handleSearchClick}
          />
        )}
        {searchOpen && (
          <div
            className={classnames(styles.searchInput, {
              [styles.searchInputRelative]: !showSubHeader,
            })}
          >
            <SearchInput onClose={handleCloseClick} text={searchText} onChange={handleSearch} />
          </div>
        )}
      </div>
      {showSelectAll &&
        selectAllLimit > 0 &&
        selectAllRemainCount > 0 &&
        mappableEventNumber > 0 &&
        activeFilter !== ETableMappedFilter.Mapped && (
          <div className={styles.selectAllContainer}>
            <Tooltip
              title={`Remaining ${selectAllRemainCount} events can be created`}
              mouseEnterDelay={1}
              placement="topLeft"
            >
              <Checkbox
                borderColor={'#0046ff'}
                onClick={(e) => e.stopPropagation()}
                onChange={handleSelectAllClick}
                checked={selectAllChecked}
                style={{ marginRight: 6 }}
              />
              {`select all (${finalSelectAllCount})`}
            </Tooltip>
          </div>
        )}
      <div style={{ width: '100%', height: '100%' }}>{getTableComponent()}</div>
    </div>
  );
};

export default Table;
